import { useState } from "react";
import { getStraightPath, useNodes, useEdges, useReactFlow, EdgeLabelRenderer, type EdgeProps } from "@xyflow/react";
import { useCanvasStore, type KFEdge, type KFEdgeData, type KFNode, type EdgeKind } from "@/store/useCanvasStore";
import { THEME } from "@/config/theme";
import { CV } from "@/config/canvasSizing";
import { getFloatingEdgePoints, resolveEndpointDrop } from "./edgeUtils";
import {
  EDGE_MARKER_ID,
  EDGE_MARKER_ACTIVE_ID,
  EDGE_MARKER_START_ID,
  EDGE_MARKER_START_ACTIVE_ID,
} from "./EdgeMarkers";

// ── Kind → visual style ───────────────────────────────────────────────────────

interface KindStyle {
  stroke: string;
  strokeDasharray?: string;
  dotColor: string;
}

const KIND_STYLE: Record<EdgeKind | "default", KindStyle> = {
  causes:       { stroke: THEME.raw.edgeStroke, dotColor: THEME.raw.edgeDotColor },
  "part-of":    { stroke: THEME.raw.edgeStroke, strokeDasharray: "4 4", dotColor: THEME.raw.edgeDotColor },
  "leads-to":   { stroke: THEME.raw.edgeStroke, strokeDasharray: "8 3", dotColor: THEME.raw.edgeDotColor },
  "answered-by":{ stroke: THEME.raw.edgeStroke, dotColor: THEME.raw.edgeDotColor },
  default:      { stroke: THEME.raw.edgeStroke, dotColor: THEME.raw.edgeDotColor },
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function AnimatedEdge({
  id,
  source,
  target,
  data,
  style,
  selected,
}: EdgeProps<KFEdge>) {
  // useNodes returns a stable array — no Map mutation detection issues
  const nodes = useNodes<KFNode>();
  const edges = useEdges<KFEdge>();
  const { screenToFlowPosition, getNodes } = useReactFlow<KFNode>();
  const reconnectEdge     = useCanvasStore((s) => s.reconnectEdge);
  const detachEdgeToShape = useCanvasStore((s) => s.detachEdgeToShape);
  const selectedIds       = useCanvasStore((s) => s.selectedIds);
  // Hand mode doesn't select — clicking a node "peeks" it via focusNodeId. Treat a
  // peeked endpoint the same as a selected one so the highlight works in hand mode.
  const focusNodeId       = useCanvasStore((s) => s.focusNodeId);
  // Live position of the endpoint being dragged (flow coords), for the preview.
  const [drag, setDrag] = useState<{ which: "source" | "target"; x: number; y: number } | null>(null);
  const sourceNode = nodes.find((n) => n.id === source);
  const targetNode = nodes.find((n) => n.id === target);

  if (!sourceNode || !targetNode) return null;

  const { sourceX, sourceY, targetX, targetY } =
    getFloatingEdgePoints(sourceNode, targetNode);

  // Straight, architectural edges (design mock) — a direct line between the two
  // node borders, not a bezier curve.
  const [edgePath] = getStraightPath({ sourceX, sourceY, targetX, targetY });

  const d = data as KFEdgeData | undefined;
  const flowSpeed = d?.flowSpeed ?? 0;
  const kind = (d?.kind ?? "default") as EdgeKind | "default";
  const ks = KIND_STYLE[kind] ?? KIND_STYLE.default;
  const bidirectional = !!d?.bidirectional;

  // Active edge (design mock): brighter + thicker + faster flow when the edge
  // itself or either connected node is selected.
  const active =
    !!selected ||
    selectedIds.has(source) || selectedIds.has(target) ||
    focusNodeId === source || focusNodeId === target;
  const edgeStroke = active ? THEME.raw.edgeStrokeSelected : ks.stroke;
  const edgeStrokeWidth = active ? THEME.raw.edgeStrokeWidthSelected : THEME.raw.edgeStrokeWidth;

  // Arrowheads follow the same highlight as the line (idle vs active variant).
  // Computed here — not taken from the RF markerEnd/markerStart props — so a node
  // selection or hand-mode peek brightens the head together with the line.
  const endMarkerId = active ? EDGE_MARKER_ACTIVE_ID : EDGE_MARKER_ID;
  const startMarkerId = active ? EDGE_MARKER_START_ACTIVE_ID : EDGE_MARKER_START_ID;
  const markerEndUrl = `url(#${endMarkerId})`;
  const markerStartUrl = bidirectional ? `url(#${startMarkerId})` : undefined;

  // Draw-in on reveal (design mock): the line grows from source to target as its
  // nodes light up. Viewport tags reveal-mode edges with `revealVisible`
  // (undefined outside reveal → no draw-in). Skip kind-dashed edges so we don't
  // clobber their dash pattern.
  const revealVisible = (data as unknown as { revealVisible?: boolean } | undefined)?.revealVisible;
  const drawIn = revealVisible !== undefined && !ks.strokeDasharray;
  const edgeLen = Math.hypot(targetX - sourceX, targetY - sourceY);

  // Round trip covers 2x the path length in the same visual "speed" —
  // double the duration so a bidirectional edge doesn't look sped up.
  const animDuration = flowSpeed > 0
    ? Math.max(0.8, 3 / flowSpeed) * (bidirectional ? 2 : 1) * (active ? 0.65 : 1)
    : 0;

  // Auto-propagate: inherit label from any upstream edge feeding into source node
  const incomingLabel = edges
    .find((e) => e.target === source && e.data?.flowLabel)
    ?.data?.flowLabel;
  const effectiveLabel = d?.flowLabel || incomingLabel;

  // Drag one end of a selected edge: onto another node → reconnect that end;
  // onto empty canvas → detach the whole edge into a free-floating arrow.
  const onEndpointDown = (which: "source" | "target") => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const fixedNodeId = which === "source" ? target : source;
    let moved = false;
    function onMove(ev: MouseEvent) {
      moved = true;
      const p = screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
      setDrag({ which, x: p.x, y: p.y });
    }
    function onUp(ev: MouseEvent) {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDrag(null);
      // A plain click (no drag) must not reconnect/detach — leave the edge as-is.
      if (!moved) return;
      const p = screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
      const drop = resolveEndpointDrop(p, getNodes(), fixedNodeId);
      if (drop.type === "reconnect") {
        if (which === "source") reconnectEdge(id, drop.nodeId, target);
        else reconnectEdge(id, source, drop.nodeId);
      } else {
        detachEdgeToShape(
          id,
          which === "source" ? p.x : sourceX,
          which === "source" ? p.y : sourceY,
          which === "target" ? p.x : targetX,
          which === "target" ? p.y : targetY,
        );
      }
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleR = CV.edge.endpointHandleRadius;
  const srcHandle = drag?.which === "source" ? { x: drag.x, y: drag.y } : { x: sourceX, y: sourceY };
  const tgtHandle = drag?.which === "target" ? { x: drag.x, y: drag.y } : { x: targetX, y: targetY };

  const handleD = handleR * 2;
  const handleStyle: React.CSSProperties = {
    position: "absolute",
    width: handleD,
    height: handleD,
    borderRadius: "9999px",
    background: THEME.raw.edgeConnection,
    border: `1px solid ${THEME.raw.handleBorder}`,
    pointerEvents: "all",
    cursor: "move",
  };

  return (
    <>
    <g style={style}>
      {/* Wide invisible hitbox — explicit pointer-events so clicks always register */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={THEME.raw.edgeHitboxWidth}
        style={{ pointerEvents: "stroke", cursor: "pointer" }}
      />
      {/* Visible edge path */}
      <path
        id={id}
        d={edgePath}
        fill="none"
        strokeOpacity={active ? THEME.raw.edgeOpacitySelected : THEME.raw.edgeOpacity}
        strokeWidth={edgeStrokeWidth}
        stroke={edgeStroke}
        strokeLinecap="round"
        strokeDasharray={drawIn ? edgeLen : ks.strokeDasharray}
        strokeDashoffset={drawIn ? (revealVisible ? 0 : edgeLen) : undefined}
        markerEnd={markerEndUrl}
        markerStart={markerStartUrl}
        className="react-flow__edge-path"
        style={drawIn ? { pointerEvents: "none", transition: "stroke-dashoffset 0.7s ease" } : { pointerEvents: "none" }}
      />
      {/* Animated flow dot */}
      {flowSpeed > 0 && (
        <g style={{ pointerEvents: "none" }}>
          <animateMotion
            dur={`${animDuration}s`}
            repeatCount="indefinite"
            path={edgePath}
            {...(bidirectional
              ? { keyPoints: "0;1;0", keyTimes: "0;0.5;1", calcMode: "linear" }
              : {})}
          />
          <circle r={CV.edge.flowDotRadius} fill={ks.dotColor} opacity="0.85" />
          {effectiveLabel && (
            <text
              y={-(CV.edge.flowDotRadius + 4)}
              textAnchor="middle"
              fontSize="9"
              fontWeight="600"
              fill={ks.dotColor}
              opacity="0.9"
            >
              {effectiveLabel}
            </text>
          )}
        </g>
      )}
      {/* Dashed drag preview from the fixed end to the cursor (flow coords). */}
      {selected && drag && (
        <line
          x1={drag.which === "source" ? tgtHandle.x : srcHandle.x}
          y1={drag.which === "source" ? tgtHandle.y : srcHandle.y}
          x2={drag.x}
          y2={drag.y}
          stroke={THEME.raw.edgeConnection}
          strokeWidth={THEME.raw.edgeStrokeWidth}
          strokeDasharray="5 4"
          style={{ pointerEvents: "none" }}
        />
      )}
    </g>
    {/* Draggable endpoint handles — reconnect onto a node or detach into a free
        arrow. Rendered in EdgeLabelRenderer (a DOM layer ABOVE the nodes) so the
        node body and its connection handles can't occlude them or hijack the
        drag into a brand-new edge. Shown while the edge is selected. */}
    {selected && (
      <EdgeLabelRenderer>
        <div
          data-testid="edge-endpoint-source"
          className="nodrag nopan"
          style={{ ...handleStyle, transform: `translate(-50%, -50%) translate(${srcHandle.x}px, ${srcHandle.y}px)` }}
          onMouseDown={onEndpointDown("source")}
        />
        <div
          data-testid="edge-endpoint-target"
          className="nodrag nopan"
          style={{ ...handleStyle, transform: `translate(-50%, -50%) translate(${tgtHandle.x}px, ${tgtHandle.y}px)` }}
          onMouseDown={onEndpointDown("target")}
        />
      </EdgeLabelRenderer>
    )}
    </>
  );
}
